import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useVenues } from '../contexts/VenueContext';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../contexts/GlobalSettingsContext'; // Import Global Settings
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCode, FiCopy, FiLink, FiSettings, FiEye, FiGlobe, FiAlertCircle } = FiIcons;

const WidgetSettingsPage = () => {
  const { venues } = useVenues();
  const { user } = useAuth();
  const { settings } = useGlobalSettings(); // Get settings
  const [selectedVenue, setSelectedVenue] = useState('');
  
  // Link Button Settings (Visuals only for preview)
  const [buttonSettings, setButtonSettings] = useState({
    text: 'Book Now',
    backgroundColor: '#2563EB', // Blue-600
    textColor: '#ffffff',
    borderRadius: '8',
    padding: '12px 24px',
    fontSize: '16',
    fontWeight: 'bold'
  });

  // Automatically select the first venue when venues are loaded
  useEffect(() => {
    if (venues.length > 0 && !selectedVenue) {
      setSelectedVenue(venues[0].id);
    }
  }, [venues, selectedVenue]);

  const venue = venues.find(v => v.id === selectedVenue);
  
  // UPDATED: Use dynamic URL from settings
  const PRODUCTION_URL = settings.productionUrl || 'https://site2rent.netlify.app';

  const bookingPageUrl = selectedVenue && user
    ? `${PRODUCTION_URL}/#/book/${user.id}/${selectedVenue}`
    : 'Select a venue first...';

  const generateCode = () => {
    if (!selectedVenue || !user) return '<!-- Please select a venue first -->';
    
    return `<!-- Booking Button for ${venue?.name || 'Venue'} -->
<a href="${bookingPageUrl}" target="_blank" style="display: inline-block; background-color: ${buttonSettings.backgroundColor}; color: ${buttonSettings.textColor}; padding: ${buttonSettings.padding}; border-radius: ${buttonSettings.borderRadius}px; text-decoration: none; font-family: system-ui, -apple-system, sans-serif; font-size: ${buttonSettings.fontSize}px; font-weight: ${buttonSettings.fontWeight}; transition: opacity 0.2s ease; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
  ${buttonSettings.text}
</a>`;
  };

  const copyToClipboard = (text, buttonId) => {
    if (!selectedVenue) return;
    navigator.clipboard.writeText(text);
    const button = document.getElementById(buttonId);
    if (!button) return;
    
    const originalContent = button.innerHTML;
    button.innerHTML = '<span class="flex items-center"><svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>Copied!</span>';
    button.classList.remove('bg-blue-600', 'hover:bg-blue-700', 'bg-white', 'text-gray-700');
    button.classList.add('bg-green-600', 'hover:bg-green-700', 'text-white');
    
    setTimeout(() => {
      button.innerHTML = originalContent;
      button.classList.remove('bg-green-600', 'hover:bg-green-700', 'text-white');
      if (buttonId === 'copy-url-btn') {
         button.classList.add('bg-white', 'text-gray-700', 'hover:bg-gray-50'); 
      } else {
         button.classList.add('bg-blue-600', 'hover:bg-blue-700', 'text-white');
      }
    }, 2000);
  };

  const updateButtonSetting = (key, value) => {
    setButtonSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Booking Widgets</h1>
            <p className="text-gray-600 mt-1">Get the link or code for your website</p>
          </div>
        </div>

        {venues.length === 0 ? (
           <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
             <SafeIcon icon={FiAlertCircle} className="mx-auto h-12 w-12 text-yellow-500 mb-2" />
             <h3 className="text-lg font-bold text-gray-900">No Venues Found</h3>
             <p className="text-gray-600">Please create a venue in the Dashboard first to generate a booking link.</p>
           </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Settings Panel */}
            <div className="space-y-6">
              
              {/* 1. Venue Selection */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50 flex items-center">
                  <SafeIcon icon={FiSettings} className="mr-2 h-5 w-5 text-gray-500" />
                  <h2 className="font-semibold text-gray-900">1. Select Venue</h2>
                </div>
                <div className="p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Venue to Link</label>
                  <select 
                    value={selectedVenue}
                    onChange={(e) => setSelectedVenue(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="" disabled>Select a venue...</option>
                    {venues.map(venue => (
                      <option key={venue.id} value={venue.id}>{venue.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 2. Direct Link */}
              <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-100 overflow-hidden">
                 <div className="p-6 border-b border-blue-100 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-blue-600 p-1.5 rounded-lg mr-3">
                       <SafeIcon icon={FiLink} className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-bold text-blue-900">Direct Booking Link</h2>
                      <p className="text-xs text-blue-700">Use this for Wix, Squarespace, WordPress buttons</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      readOnly 
                      value={bookingPageUrl} 
                      className="w-full px-3 py-2.5 border border-blue-200 rounded-lg bg-white text-gray-600 text-sm focus:outline-none" 
                    />
                    <button 
                      id="copy-url-btn"
                      onClick={() => copyToClipboard(bookingPageUrl, 'copy-url-btn')}
                      disabled={!selectedVenue}
                      className="bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 px-4 py-2.5 rounded-lg font-semibold transition-all shadow-sm flex items-center whitespace-nowrap"
                    >
                      <SafeIcon icon={FiCopy} className="mr-2 h-4 w-4" />
                      Copy Link
                    </button>
                  </div>
                  <div className="mt-4 flex items-start gap-2 text-xs text-blue-800">
                    <SafeIcon icon={FiGlobe} className="mt-0.5 h-4 w-4" />
                    <p><strong>Instructions:</strong> In your CMS editor, click on your "Book Now" button, select "Link" or "URL", and paste this link.</p>
                  </div>
                </div>
              </div>

              {/* 3. HTML Code */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                 <div className="p-6 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCode} className="mr-2 h-5 w-5 text-gray-500" />
                    <h2 className="font-semibold text-gray-900">HTML Code (Advanced)</h2>
                  </div>
                  <button 
                    id="copy-code-btn"
                    onClick={() => copyToClipboard(generateCode(), 'copy-code-btn')}
                    disabled={!selectedVenue}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                  >
                    Copy HTML
                  </button>
                </div>
                <div className="p-4 bg-gray-900 relative group">
                  <pre className="text-green-400 text-sm font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed h-32">
                    <code>{generateCode()}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-24">
                <div className="p-6 border-b border-gray-100 bg-gray-50 flex items-center">
                  <SafeIcon icon={FiEye} className="mr-2 h-5 w-5 text-gray-500" />
                  <h2 className="font-semibold text-gray-900">Live Preview</h2>
                </div>

                <div className="p-8">
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your Website Content</h3>
                    <p className="text-gray-500 mb-8 max-w-sm">
                      "Experience luxury living in the heart of the city. Book your stay today!"
                    </p>
                    
                    <a 
                      href={selectedVenue ? bookingPageUrl : '#'} 
                      target="_blank" 
                      rel="noreferrer"
                      style={{
                        backgroundColor: buttonSettings.backgroundColor,
                        color: buttonSettings.textColor,
                        padding: buttonSettings.padding,
                        borderRadius: buttonSettings.borderRadius + 'px',
                        textDecoration: 'none',
                        fontSize: buttonSettings.fontSize + 'px',
                        fontWeight: buttonSettings.fontWeight,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        display: 'inline-block',
                        transition: 'all 0.2s ease',
                        cursor: selectedVenue ? 'pointer' : 'not-allowed',
                        opacity: selectedVenue ? 1 : 0.5
                      }}
                      onMouseOver={(e) => selectedVenue && (e.target.style.opacity = '0.9')}
                      onMouseOut={(e) => selectedVenue && (e.target.style.opacity = '1')}
                    >
                      {buttonSettings.text}
                    </a>

                    <p className="mt-8 text-xs text-gray-400">
                      Clicking this will open the booking page for <br/>
                      <strong className="text-gray-600">{venue?.name || '...'}</strong>
                    </p>
                  </div>
                  
                  {/* Button Style Settings */}
                  <div className="mt-6 border-t border-gray-100 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Customize Button Appearance</h4>
                    <div className="grid grid-cols-2 gap-4">
                       <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Button Text</label>
                        <input 
                          type="text" 
                          value={buttonSettings.text} 
                          onChange={(e) => updateButtonSetting('text', e.target.value)} 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Background</label>
                        <input 
                            type="color" 
                            value={buttonSettings.backgroundColor} 
                            onChange={(e) => updateButtonSetting('backgroundColor', e.target.value)} 
                            className="h-8 w-12 border border-gray-300 rounded cursor-pointer" 
                          />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Text Color</label>
                        <input 
                            type="color" 
                            value={buttonSettings.textColor} 
                            onChange={(e) => updateButtonSetting('textColor', e.target.value)} 
                            className="h-8 w-12 border border-gray-300 rounded cursor-pointer" 
                          />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WidgetSettingsPage;